/**
 * Public crowd predictions.
 *
 * Ballots stay inside private parties. This module turns them into a show-level
 * share that is safe to publish:
 * - one settled ballot per person per category (latest pick wins)
 * - a category stays hidden until enough people, across enough parties, have voted
 * - a category stays hidden when one party is a strict majority of that sample
 * - the public total is rounded down, and picks from the last hour are left out
 * - the result never includes a user, party, or vote id
 */

export const COLLABORATIVE_PREDICTION_MIN_VOTERS = 10;
export const COLLABORATIVE_PREDICTION_MIN_PARTIES = 3;
export const COLLABORATIVE_PREDICTION_VOTER_BUCKET = 5;
export const COLLABORATIVE_PREDICTION_VOTE_LAG_MS = 60 * 60 * 1000;

export type CollaborativePredictionWithheldReason =
  | "too_few_voters"
  | "too_few_parties"
  | "single_party_majority";

export type CollaborativeVoteRecord = {
  voteId: string;
  userId: string;
  partyId: string;
  categoryId: string;
  nomineeId: string;
  updatedAt: string;
};

export type CollaborativeNomineeBallot = {
  id: string;
  name: string;
  additionalInfo: string | null;
  imageUrl: string | null;
};

export type CollaborativeCategoryBallot = {
  id: string;
  name: string;
  fieldName: string | null;
  orderIndex: number;
  nominees: CollaborativeNomineeBallot[];
  winnerNomineeIds: string[];
};

export type CollaborativePredictionNominee = {
  nomineeId: string;
  name: string;
  additionalInfo: string | null;
  imageUrl: string | null;
  sharePercent: number;
  isPick: boolean;
  isWinner: boolean;
};

export type CollaborativePredictionCategory = {
  categoryId: string;
  name: string;
  fieldName: string | null;
  orderIndex: number;
  voterBucket: number;
  nominees: CollaborativePredictionNominee[];
  pickNomineeIds: string[];
};

export type CollaborativePredictionWithheldCategory = {
  categoryId: string;
  reason: CollaborativePredictionWithheldReason;
};

export type CollaborativePredictionHeadline = {
  categoryName: string;
  nomineeName: string;
  sharePercent: number;
};

export type AggregateCollaborativePredictionsInput = {
  categories: CollaborativeCategoryBallot[];
  votes: CollaborativeVoteRecord[];
  now?: Date;
};

export type AggregateCollaborativePredictionsResult = {
  categories: CollaborativePredictionCategory[];
  withheld: CollaborativePredictionWithheldCategory[];
};

type CountedVote = {
  userId: string;
  partyId: string;
  nomineeId: string;
  updatedAtMs: number;
  voteId: string;
};

export function aggregateCollaborativePredictions(
  input: AggregateCollaborativePredictionsInput,
): AggregateCollaborativePredictionsResult {
  const nowMs = (input.now ?? new Date()).getTime();
  const settledBeforeMs = nowMs - COLLABORATIVE_PREDICTION_VOTE_LAG_MS;
  const categories = [...input.categories].sort(
    (left, right) => left.orderIndex - right.orderIndex || left.name.localeCompare(right.name),
  );
  const published: CollaborativePredictionCategory[] = [];
  const withheld: CollaborativePredictionWithheldCategory[] = [];

  for (const category of categories) {
    const outcome = aggregateCategory(category, input.votes, settledBeforeMs);
    if (outcome.kind === "publish") {
      published.push(outcome.category);
    } else if (outcome.kind === "withhold") {
      withheld.push({ categoryId: category.id, reason: outcome.reason });
    }
  }

  return { categories: published, withheld };
}

export function collaborativePredictionHeadline(
  categories: CollaborativePredictionCategory[],
): CollaborativePredictionHeadline | null {
  const ranked = [...categories].sort((left, right) => {
    const leftShare = topShare(left);
    const rightShare = topShare(right);
    if (leftShare !== rightShare) {
      return rightShare - leftShare;
    }
    return left.orderIndex - right.orderIndex || left.name.localeCompare(right.name);
  });
  const category = ranked[0];
  if (!category || category.pickNomineeIds.length === 0) {
    return null;
  }
  const picks = category.nominees.filter((nominee) => nominee.isPick);
  const sharePercent = picks[0]?.sharePercent ?? 0;
  if (sharePercent <= 0) {
    return null;
  }
  return {
    categoryName: category.name,
    nomineeName: picks.map((nominee) => nominee.name).join(" / "),
    sharePercent,
  };
}

export const COLLABORATIVE_PREDICTION_LOCK_SHARE = 60;
export const COLLABORATIVE_PREDICTION_TOSSUP_MARGIN = 10;

export type CollaborativePredictionConfidence = "lock" | "favorite" | "tossup";
export type CollaborativePredictionOutcome = "pending" | "called" | "upset";

export type CollaborativePredictionScorecard = {
  decided: number;
  called: number;
};

export type CollaborativePredictionHighlights = {
  locks: CollaborativePredictionCategory[];
  tossUps: CollaborativePredictionCategory[];
};

export type CollaborativePredictionRecap = {
  called: CollaborativePredictionCategory[];
  missed: CollaborativePredictionCategory[];
};

/** Points between the leader and the runner-up. */
export function collaborativePredictionMargin(
  category: CollaborativePredictionCategory,
): number {
  const [first, second] = category.nominees
    .map((nominee) => nominee.sharePercent)
    .sort((left, right) => right - left);
  return (first ?? 0) - (second ?? 0);
}

export function collaborativePredictionConfidence(
  category: CollaborativePredictionCategory,
): CollaborativePredictionConfidence {
  if (category.pickNomineeIds.length !== 1) {
    return "tossup";
  }
  if (topShare(category) >= COLLABORATIVE_PREDICTION_LOCK_SHARE) {
    return "lock";
  }
  if (collaborativePredictionMargin(category) < COLLABORATIVE_PREDICTION_TOSSUP_MARGIN) {
    return "tossup";
  }
  return "favorite";
}

export function collaborativePredictionOutcome(
  category: CollaborativePredictionCategory,
): CollaborativePredictionOutcome {
  if (!category.nominees.some((nominee) => nominee.isWinner)) {
    return "pending";
  }
  return category.nominees.some((nominee) => nominee.isWinner && nominee.isPick)
    ? "called"
    : "upset";
}

export function collaborativePredictionScorecard(
  categories: CollaborativePredictionCategory[],
): CollaborativePredictionScorecard {
  let decided = 0;
  let called = 0;
  for (const category of categories) {
    const outcome = collaborativePredictionOutcome(category);
    if (outcome === "pending") {
      continue;
    }
    decided += 1;
    if (outcome === "called") {
      called += 1;
    }
  }
  return { decided, called };
}

/** Strongest consensus picks and tightest races, each capped at `limit`. */
export function rankCollaborativePredictionHighlights(
  categories: CollaborativePredictionCategory[],
  limit = 3,
): CollaborativePredictionHighlights {
  const byOrder = (
    left: CollaborativePredictionCategory,
    right: CollaborativePredictionCategory,
  ) => left.orderIndex - right.orderIndex || left.name.localeCompare(right.name);

  const locks = categories
    .filter((category) => collaborativePredictionConfidence(category) === "lock")
    .sort(
      (left, right) =>
        topShare(right) - topShare(left) ||
        collaborativePredictionMargin(right) - collaborativePredictionMargin(left) ||
        byOrder(left, right),
    )
    .slice(0, limit);

  const tossUps = categories
    .filter(
      (category) =>
        category.nominees.length > 1 &&
        collaborativePredictionOutcome(category) === "pending" &&
        collaborativePredictionConfidence(category) === "tossup",
    )
    .sort(
      (left, right) =>
        collaborativePredictionMargin(left) - collaborativePredictionMargin(right) ||
        byOrder(left, right),
    )
    .slice(0, limit);

  return { locks, tossUps };
}

/** Finished shows: strongest correct calls and most confident misses. */
export function rankCollaborativePredictionRecap(
  categories: CollaborativePredictionCategory[],
  limit = 3,
): CollaborativePredictionRecap {
  const byOrder = (
    left: CollaborativePredictionCategory,
    right: CollaborativePredictionCategory,
  ) => left.orderIndex - right.orderIndex || left.name.localeCompare(right.name);

  const byStrength = (
    left: CollaborativePredictionCategory,
    right: CollaborativePredictionCategory,
  ) =>
    topShare(right) - topShare(left) ||
    collaborativePredictionMargin(right) - collaborativePredictionMargin(left) ||
    byOrder(left, right);

  const called = categories
    .filter((category) => collaborativePredictionOutcome(category) === "called")
    .sort(byStrength)
    .slice(0, limit);

  const missed = categories
    .filter((category) => collaborativePredictionOutcome(category) === "upset")
    .sort(byStrength)
    .slice(0, limit);

  return { called, missed };
}

export type CollaborativePredictionShowStatus =
  | "live"
  | "upcoming"
  | "past"
  | "complete";

/**
 * New ballots only count before a show starts. Live, past, and final
 * pages are a read of picks that have already closed.
 */
export function collaborativePredictionPicksOpen(
  status: CollaborativePredictionShowStatus,
): boolean {
  return status === "upcoming";
}

export type CollaborativePredictionTopPick = {
  categoryId: string;
  categoryName: string;
  nomineeName: string;
  imageUrl: string | null;
  sharePercent: number;
};

export type CollaborativePredictionShowSummary = {
  showId: string;
  name: string;
  year: number;
  startTime: string | null;
  imageUrl: string | null;
  status: CollaborativePredictionShowStatus;
  publishedCategoryCount: number;
  withheldCategoryCount: number;
  ballotBucket: number;
  headline: CollaborativePredictionHeadline | null;
  topPicks: CollaborativePredictionTopPick[];
  scorecard: CollaborativePredictionScorecard;
};

export type CollaborativePredictionShow = CollaborativePredictionShowSummary & {
  categories: CollaborativePredictionCategory[];
};

/** Crowd share shown on a private ballot. Only published nominees appear. */
export type BallotCrowdShare = {
  sharePercent: number;
  isPick: boolean;
};

/**
 * Lookup of public crowd shares by nominee id.
 * Categories that failed the publish rules are simply absent.
 */
export function ballotCrowdSharesByNominee(
  categories: CollaborativePredictionCategory[],
): Record<string, BallotCrowdShare> {
  const shares: Record<string, BallotCrowdShare> = {};
  for (const category of categories) {
    for (const nominee of category.nominees) {
      shares[nominee.nomineeId] = {
        sharePercent: nominee.sharePercent,
        isPick: nominee.isPick,
      };
    }
  }
  return shares;
}

/** Strongest single (untied) crowd picks, strongest first. */
export function collaborativePredictionTopPicks(
  categories: CollaborativePredictionCategory[],
  limit = 3,
): CollaborativePredictionTopPick[] {
  return categories
    .filter((category) => category.pickNomineeIds.length === 1 && topShare(category) > 0)
    .sort(
      (left, right) =>
        topShare(right) - topShare(left) ||
        left.orderIndex - right.orderIndex ||
        left.name.localeCompare(right.name),
    )
    .slice(0, limit)
    .flatMap((category) => {
      const pick = category.nominees.find((nominee) => nominee.isPick);
      return pick
        ? [
            {
              categoryId: category.categoryId,
              categoryName: category.name,
              nomineeName: pick.name,
              imageUrl: pick.imageUrl,
              sharePercent: pick.sharePercent,
            },
          ]
        : [];
    });
}

/** Rounded-down public ballot count for a show: its largest published sample. */
export function collaborativePredictionBallotBucket(
  categories: CollaborativePredictionCategory[],
): number {
  return categories.reduce((max, category) => Math.max(max, category.voterBucket), 0);
}

const SHOW_STATUS_RANK: Record<CollaborativePredictionShowStatus, number> = {
  live: 0,
  upcoming: 1,
  past: 2,
  complete: 2,
};

/** Live first, then upcoming soonest-first, then finished most-recent-first. */
export function orderCollaborativePredictionShows<
  T extends {
    status: CollaborativePredictionShowStatus;
    startTime: string | null;
    name: string;
  },
>(shows: T[]): T[] {
  const startMs = (value: string | null) => {
    const ms = value ? Date.parse(value) : Number.NaN;
    return Number.isNaN(ms) ? null : ms;
  };
  return [...shows].sort((left, right) => {
    const rank = SHOW_STATUS_RANK[left.status] - SHOW_STATUS_RANK[right.status];
    if (rank !== 0) {
      return rank;
    }
    const leftMs = startMs(left.startTime);
    const rightMs = startMs(right.startTime);
    if (leftMs !== null && rightMs !== null && leftMs !== rightMs) {
      return SHOW_STATUS_RANK[left.status] === 1 ? leftMs - rightMs : rightMs - leftMs;
    }
    if (leftMs !== null && rightMs === null) {
      return -1;
    }
    if (leftMs === null && rightMs !== null) {
      return 1;
    }
    return left.name.localeCompare(right.name);
  });
}

export function bucketCollaborativeVoterCount(voterCount: number): number {
  if (voterCount < COLLABORATIVE_PREDICTION_VOTER_BUCKET) {
    return 0;
  }
  return (
    Math.floor(voterCount / COLLABORATIVE_PREDICTION_VOTER_BUCKET) *
    COLLABORATIVE_PREDICTION_VOTER_BUCKET
  );
}

function aggregateCategory(
  category: CollaborativeCategoryBallot,
  votes: CollaborativeVoteRecord[],
  settledBeforeMs: number,
):
  | { kind: "publish"; category: CollaborativePredictionCategory }
  | { kind: "withhold"; reason: CollaborativePredictionWithheldReason }
  | { kind: "skip" } {
  const nomineeIds = new Set(category.nominees.map((nominee) => nominee.id));
  const counted = new Map<string, CountedVote>();

  for (const vote of votes) {
    if (vote.categoryId !== category.id || !nomineeIds.has(vote.nomineeId)) {
      continue;
    }
    const updatedAtMs = Date.parse(vote.updatedAt);
    if (Number.isNaN(updatedAtMs) || updatedAtMs > settledBeforeMs) {
      continue;
    }
    const current = counted.get(vote.userId);
    if (
      !current ||
      updatedAtMs > current.updatedAtMs ||
      (updatedAtMs === current.updatedAtMs && vote.voteId > current.voteId)
    ) {
      counted.set(vote.userId, {
        userId: vote.userId,
        partyId: vote.partyId,
        nomineeId: vote.nomineeId,
        updatedAtMs,
        voteId: vote.voteId,
      });
    }
  }

  if (counted.size === 0) {
    return { kind: "skip" };
  }
  if (counted.size < COLLABORATIVE_PREDICTION_MIN_VOTERS) {
    return { kind: "withhold", reason: "too_few_voters" };
  }

  const votersByParty = new Map<string, number>();
  const votersByNominee = new Map<string, number>();
  for (const vote of counted.values()) {
    votersByParty.set(vote.partyId, (votersByParty.get(vote.partyId) ?? 0) + 1);
    votersByNominee.set(
      vote.nomineeId,
      (votersByNominee.get(vote.nomineeId) ?? 0) + 1,
    );
  }

  if (votersByParty.size < COLLABORATIVE_PREDICTION_MIN_PARTIES) {
    return { kind: "withhold", reason: "too_few_parties" };
  }

  const largestParty = Math.max(...votersByParty.values());
  if (largestParty * 2 > counted.size) {
    return { kind: "withhold", reason: "single_party_majority" };
  }

  const counts = category.nominees.map(
    (nominee) => votersByNominee.get(nominee.id) ?? 0,
  );
  const sharePercents = largestRemainderPercents(counts);
  const topPercent = Math.max(...sharePercents);
  const nominees = category.nominees
    .map((nominee, index) => ({
      nomineeId: nominee.id,
      name: nominee.name,
      additionalInfo: nominee.additionalInfo,
      imageUrl: nominee.imageUrl,
      sharePercent: sharePercents[index] ?? 0,
      isWinner: category.winnerNomineeIds.includes(nominee.id),
      isPick: false,
    }))
    .sort(
      (left, right) =>
        right.sharePercent - left.sharePercent ||
        left.name.localeCompare(right.name) ||
        left.nomineeId.localeCompare(right.nomineeId),
    )
    .map((nominee) => ({
      ...nominee,
      isPick: topPercent > 0 && nominee.sharePercent === topPercent,
    }));

  return {
    kind: "publish",
    category: {
      categoryId: category.id,
      name: category.name,
      fieldName: category.fieldName,
      orderIndex: category.orderIndex,
      voterBucket: bucketCollaborativeVoterCount(counted.size),
      nominees,
      pickNomineeIds: nominees
        .filter((nominee) => nominee.isPick)
        .map((nominee) => nominee.nomineeId),
    },
  };
}

function largestRemainderPercents(counts: number[]): number[] {
  const total = counts.reduce((sum, count) => sum + count, 0);
  if (total <= 0) {
    return counts.map(() => 0);
  }
  const exact = counts.map((count) => (count * 100) / total);
  const floors = exact.map((value) => Math.floor(value));
  let remainder = 100 - floors.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort(
      (left, right) =>
        right.fraction - left.fraction || left.index - right.index,
    );
  const percents = [...floors];
  for (const entry of order) {
    if (remainder <= 0) {
      break;
    }
    percents[entry.index] = (percents[entry.index] ?? 0) + 1;
    remainder -= 1;
  }
  return percents;
}

function topShare(category: CollaborativePredictionCategory): number {
  return category.nominees.reduce(
    (max, nominee) => (nominee.isPick ? Math.max(max, nominee.sharePercent) : max),
    0,
  );
}
